import { Application, Container, Graphics } from "pixi.js";
import {
  DEMO_FORM,
  TILING_EMPTY,
  type TilingStimulus,
  cellsToPaint,
  tilingBlanks,
} from "@talent/tasks";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, mix, touchMin } from "./paint.js";
import { TOKEN_COLORS } from "./token.js";

/**
 * Carry the pattern on — the motif is printed, the rest of the strip is empty.
 *
 * A tray of colours sits under the strip; one of them is held, and tapping a cell paints it. This
 * is the one game here that uses a held-tool model rather than tapping the thing itself, and the
 * reason is the reason the key game does the opposite: there a tile had one right colour and
 * cycling to it was quick, here a child paints eighteen cells and cycling each one through four
 * colours would be sixty taps of pure labour.
 *
 * **Tapping a painted cell clears it.** A mistake has to be recoverable without a reset, and every
 * clear is recorded — a child who paints straight through and one who repaints half the strip both
 * finish it, and they are not the same performance.
 *
 * **The strip scrolls rather than shrinking.** Nine columns across a phone would put a cell at
 * about 36 px, which is half the size a five-year-old lands on. So the cells keep a size a finger
 * can hit and the strip slides sideways under a finger, with the motif staying pinned at the edge —
 * because the whole task is comparing what you are filling against what you were shown.
 */

export interface TilingTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface Attempt {
  painted: number[];
  clears: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateStimulus: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => TilingStimulus;
  scoreTrial: (stimulus: TilingStimulus, painted: readonly number[]) => boolean;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  startAt?: number;
}

/**
 * The beat after the last cell before the strip counts itself finished.
 *
 * Same device as the key game and for the same reason: without it, a child who mis-paints the last
 * cell has lost the trial with no way back, while every other cell was correctable. Any tap
 * restarts it.
 */
const SETTLE_MS = 1300;

/**
 * The gap between one block and the next, as a fraction of a cell.
 *
 * Half a cell rather than an ordinary gap, and it is the difference between a child seeing what to
 * do and not seeing it. At a gap the strip read as one uniform grid with some cells already
 * coloured in, so nothing said where the motif ENDED — and "repeat it" cannot be guessed until you
 * can see what "it" is.
 */
const SEAM_RATIO = 0.55;

export async function runTilingTask(opts: RunOptions): Promise<TilingTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h / 2, Math.min(w, h) * 0.55).fill({ color: accent, alpha: 0.07 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  function present(stimulus: TilingStimulus): {
    attempt: Promise<Attempt>;
    simulatePaint: (cell: number, colour: number) => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    /** Clipped, so the strip can slide without spilling over the tray or the back control. */
    const window_ = new Container();
    const mask = new Graphics();
    const stripFrames = new Graphics();
    const strip = new Container();
    /** The printed motif. Outside the scrolling container on purpose — see `layout`. */
    const motifFrame = new Graphics();
    const motifPane = new Container();
    const tray = new Container();
    window_.addChild(strip);
    window_.mask = mask;
    layer.addChild(motifFrame, motifPane, mask, window_, tray);

    const painted = [...stimulus.given];
    const blanks = tilingBlanks(stimulus);
    /** Which colours the motif actually uses. A tray offering an unused colour invents a wrong answer. */
    const palette = [...new Set(stimulus.solution.filter((v) => v !== TILING_EMPTY))].sort(
      (a, b) => a - b,
    );
    let held = palette[0] ?? 0;
    let clears = 0;
    let accepting = true;
    let settleToken = 0;
    let resolveAttempt: ((a: Attempt) => void) | null = null;

    let cells: Graphics[] = [];
    let swatches: Graphics[] = [];
    let cellSize = touchMin();
    let scroll = 0;
    let maxScroll = 0;
    /** Drag state, read by `onCell` to tell a scroll from a paint. */
    const DRAG_SLOP = 8;
    let dragFrom: number | null = null;
    let dragScroll = 0;
    let dragged = false;

    function drawCell(index: number): void {
      const g = cells[index];
      if (!g) return;
      const value = painted[index] ?? TILING_EMPTY;
      const isMotif = index % stimulus.cols < stimulus.motifCols;
      g.clear();

      /**
       * The motif reads as PRINTED and the rest as paper.
       *
       * Without the distinction a child cannot tell which part of the strip is the instruction, and
       * the first thing they do is repaint the motif. The printed part is darker and ringed; the
       * blank part is a pale well, the same "something goes here" the building game uses.
       */
      const ground = isMotif
        ? mix(COLOR.stage, COLOR.card, 0.2)
        : mix(COLOR.stage, COLOR.stageDeep, 0.4);
      g.roundRect(-cellSize / 2, -cellSize / 2, cellSize, cellSize, cellSize * 0.2).fill(ground);

      if (value !== TILING_EMPTY) {
        const colour = TOKEN_COLORS[value] ?? COLOR.sunny;
        const inset = cellSize * 0.11;
        g.roundRect(
          -cellSize / 2 + inset,
          -cellSize / 2 + inset,
          cellSize - inset * 2,
          cellSize - inset * 2,
          cellSize * 0.14,
        ).fill(colour);
        g.roundRect(
          -cellSize / 2 + inset * 1.6,
          -cellSize / 2 + inset * 1.5,
          (cellSize - inset * 2) * 0.5,
          (cellSize - inset * 2) * 0.22,
          cellSize * 0.06,
        ).fill({ color: 0xffffff, alpha: 0.24 });
      }

      g.roundRect(-cellSize / 2, -cellSize / 2, cellSize, cellSize, cellSize * 0.2).stroke({
        width: isMotif ? 2.5 : 1.5,
        color: isMotif ? accent : COLOR.card,
        alpha: isMotif ? 0.75 : 0.16,
      });
    }

    function drawSwatches(): void {
      swatches.forEach((g, i) => {
        const colour = TOKEN_COLORS[palette[i] as number] ?? COLOR.sunny;
        const on = palette[i] === held;
        const size = cellSize * (on ? 1.02 : 0.84);
        g.clear();
        g.roundRect(-size / 2, -size / 2, size, size, size * 0.26).fill(colour);
        if (on) {
          // The held colour is ringed rather than merely bigger: size alone is not a strong enough
          // signal on a screen where everything is already coloured.
          g.roundRect(-size / 2, -size / 2, size, size, size * 0.26).stroke({
            width: 4,
            color: COLOR.cream,
          });
        }
      });
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const gap = Math.max(4, w * 0.012);
      const topReserved = 62;
      const trayBlock = Math.max(72, h * 0.14);

      const margin = Math.max(8, w * 0.03);
      const available = w - margin * 2;
      const usableH = h - topReserved - trayBlock;

      /** How many times the motif is stamped. Always whole, by construction in `tiling.ts`. */
      const repeats = Math.max(
        1,
        Math.round((stimulus.cols - stimulus.motifCols) / stimulus.motifCols),
      );
      const blocks = repeats + 1;

      /**
       * The width the cells have to share, WITH the seams between blocks in the sum.
       *
       * The seams are a fraction of a cell, so the whole strip is linear in the cell size and this
       * solves for it directly:
       *
       *   width = cell·(blocks·motifCols + SEAM·repeats) + gap·blocks·(motifCols − 1)
       *
       * Leaving them out is what broke the first level: the seams arrived, the arithmetic did not
       * hear about them, and a three-column strip that fits a phone comfortably started scrolling.
       * A level whose whole job is to be trivially easy must not be the one that needs dragging.
       */
      const byHeight = (usableH - gap * (stimulus.rows + 1)) / stimulus.rows;
      const byWidth =
        (available - gap * blocks * (stimulus.motifCols - 1)) /
        (blocks * stimulus.motifCols + SEAM_RATIO * repeats);
      // The floor is what forces the scroll: below it a cell is a target a child misses, so the
      // strip gets wider than the screen instead of the cells getting smaller.
      cellSize = Math.max(touchMin() * 0.82, Math.min(byHeight, byWidth, 96));

      const stripH = cellSize * stimulus.rows + gap * (stimulus.rows - 1);
      const originY = topReserved + (usableH - stripH) / 2 + cellSize / 2;

      /**
       * **The motif does not scroll, and everything else here follows from that.**
       *
       * The whole task is comparing what you are filling against what you were shown, so the thing
       * you were shown has to stay on screen. The first version put every cell in one scrolling
       * container, and at the wider levels the child slid the motif off the left edge and was left
       * copying from memory — a different game, and a much harder one, arrived at by accident.
       *
       * So the motif is drawn in its own pane and only the cells past it live in the scrolling
       * container, with the mask starting where the motif ends.
       */
      /**
       * The gap between the motif and the strip it is repeated into.
       *
       * **Half a cell rather than two gaps**, and it is the difference between a child seeing what
       * to do and not. At two gaps the whole thing read as one uniform grid with some cells already
       * coloured in, so nothing said where the motif ENDED — and "repeat it" is unguessable until
       * you can see what "it" is.
       */
      const seam = cellSize * SEAM_RATIO;
      // Blocks and the seams between them, which is what the strip now measures.
      const motifW = cellSize * stimulus.motifCols + gap * (stimulus.motifCols - 1);
      const fillW = motifW * repeats + seam * (repeats - 1);

      /**
       * Left-aligned only when it has to be, centred whenever it fits.
       *
       * This shipped pinned to the left edge unconditionally, which is right on the phone the game
       * was laid out for — the strip fills the width — and looks broken anywhere wider: on the
       * teacher's preview the whole board sat in the first few hundred pixels of a very wide canvas
       * while the colour tray, which centres itself, floated alone in the middle. The two halves of
       * one control stopped looking like one control.
       *
       * So the anchor follows the same rule the scrolling does. A board that fits is centred and
       * cannot scroll; a board that does not is flush left, because that is where the motif has to
       * stay for the strip to slide past it.
       */
      /** How far a block's frame sits outside the cells it encloses. */
      const blockPad = gap * 0.9;
      const wholeW = motifW + seam + fillW;
      const fits = wholeW <= available;

      const boardX = fits ? (w - wholeW) / 2 : margin;
      const originX = boardX + cellSize / 2;
      const windowX = boardX + motifW + seam;
      const windowW = fits ? fillW : Math.max(cellSize, w - margin - windowX);
      maxScroll = Math.max(0, fillW - windowW);
      scroll = Math.min(scroll, maxScroll);

      /**
       * The strip, divided into blocks the size of the motif.
       *
       * The single change that makes this game readable without a word. The motif sits in a framed
       * block of its own; every place it has to be repeated is an empty block of exactly the same
       * size beside it. A child does not have to be told that the same thing goes in each box —
       * boxes of equal size, one of them full, say it.
       *
       * Drawn behind the cells and inside the same containers, so the motif's block stays pinned
       * with it and the repeat blocks slide with the strip.
       */
      const blockH = stripH + blockPad * 2;
      const blockY = originY - cellSize / 2 - blockPad;
      motifFrame.clear();
      motifFrame
        .roundRect(boardX - blockPad, blockY, motifW + blockPad * 2, blockH, cellSize * 0.28)
        .fill({ color: accent, alpha: 0.12 })
        .stroke({ width: 3, color: accent, alpha: 0.85 });

      stripFrames.clear();
      for (let r = 0; r < repeats; r++) {
        const x = windowX + r * (motifW + seam);
        stripFrames
          .roundRect(x - blockPad, blockY, motifW + blockPad * 2, blockH, cellSize * 0.28)
          .stroke({ width: 2, color: COLOR.cream, alpha: 0.3 });
      }

      /**
       * The window the strip slides inside, widened by the block frames' own margin.
       *
       * The frames sit `blockPad` outside the cells they enclose, so a mask measured to the cells
       * cut the last block's border off down its right-hand side — the strip ended in a box with
       * three sides. The mask has to be as wide as what is drawn, not as wide as the grid.
       */
      mask.clear();
      mask
        .rect(
          windowX - blockPad,
          topReserved - 8,
          windowW + blockPad * 2 + (fits ? 0 : margin),
          usableH + 16,
        )
        .fill(0xffffff);

      for (const child of motifPane.removeChildren()) child.destroy();
      // The block outlines belong to the strip so they slide with it, which means the rebuild below
      // has to put them back rather than destroy them with the cells.
      for (const child of strip.removeChildren()) {
        if (child !== stripFrames) child.destroy();
      }
      strip.addChild(stripFrames);
      cells = [];
      for (let index = 0; index < stimulus.cols * stimulus.rows; index++) {
        const g = new Graphics();
        const col = index % stimulus.cols;
        const row = Math.floor(index / stimulus.cols);
        const isMotif = col < stimulus.motifCols;
        g.position.set(
          isMotif
            ? originX + col * (cellSize + gap)
            : windowX +
              cellSize / 2 +
              // Inside its block by the ordinary gap, then a seam between one block and the next.
              Math.floor((col - stimulus.motifCols) / stimulus.motifCols) * (motifW + seam) +
              ((col - stimulus.motifCols) % stimulus.motifCols) * (cellSize + gap),
          originY + row * (cellSize + gap),
        );
        if (!isMotif) {
          g.eventMode = "static";
          g.cursor = "pointer";
          const half = cellSize / 2 + gap / 2;
          g.hitArea = {
            contains: (hx: number, hy: number) =>
              hx >= -half && hy >= -half && hx <= half && hy <= half,
          };
          g.on("pointertap", () => onCell(index));
        }
        (isMotif ? motifPane : strip).addChild(g);
        cells.push(g);
        drawCell(index);
      }
      strip.position.x = -scroll;

      // The tray: one swatch per colour the motif uses, along the bottom where a thumb reaches.
      for (const child of tray.removeChildren()) child.destroy();
      swatches = [];
      const trayW = cellSize * palette.length + gap * 3 * (palette.length - 1);
      // Centred on the BOARD, not on the canvas: on a wide screen a tray centred on the window sits
      // a long way from the strip it paints, and the two stop looking like one control.
      const trayX = fits ? boardX + wholeW / 2 : w / 2;
      palette.forEach((_, i) => {
        const g = new Graphics();
        g.eventMode = "static";
        g.cursor = "pointer";
        g.position.set(
          trayX - trayW / 2 + cellSize / 2 + i * (cellSize + gap * 3),
          h - trayBlock / 2,
        );
        const half = cellSize * 0.7;
        g.hitArea = {
          contains: (hx: number, hy: number) =>
            hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
        g.on("pointertap", () => {
          if (!accepting) return;
          held = palette[i] as number;
          tick();
          drawSwatches();
          void pop(g, { scale: 1.12 });
        });
        tray.addChild(g);
        swatches.push(g);
      });
      drawSwatches();
    }

    /**
     * Schedules the finish, cancelling any finish already pending.
     *
     * **Counts cells FILLED, never cells left empty**, and getting that backwards made the game
     * unfinishable. The first version waited for no blank cell to be empty — but a gap in the motif
     * repeats, so a correct strip always ends with empty cells in it. A child who did everything
     * right sat in front of a finished strip that never resolved, and their only ways out were to
     * fill every remaining cell, which scores wrong, or to wait out the three-minute timeout, which
     * is recorded as walking away. The tutorial, which skips the gaps because they are the point,
     * hung forever.
     *
     * `>=` rather than `===` for the reason the key game uses it: a child who paints one cell too
     * many has committed an answer, and an equality test would leave the trial with no way to end.
     */
    function maybeFinish(): void {
      const placed = blanks.filter((cell) => painted[cell] !== TILING_EMPTY).length;
      if (placed < cellsToPaint(stimulus)) return;
      const token = ++settleToken;
      void idle(SETTLE_MS).then(() => {
        if (token !== settleToken || !accepting) return;
        accepting = false;
        resolveAttempt?.({ painted: [...painted], clears });
      });
    }

    function onCell(index: number): void {
      if (!accepting) return;
      // A tap that travelled is a scroll, not a paint. Pixi has no threshold of its own.
      if (dragged) return;
      const g = cells[index];
      if (!g) return;

      if (painted[index] === held) {
        // Tapping a cell that already holds the held colour clears it. Recoverable without a
        // reset, and each clear is a recorded signal rather than a wasted action.
        painted[index] = TILING_EMPTY;
        clears++;
        tick();
        ripple(fx, g.position.x - scroll, g.position.y, COLOR.card, cellSize * 0.9);
      } else {
        painted[index] = held;
        cellTone(index % 7);
        ripple(fx, g.position.x - scroll, g.position.y, TOKEN_COLORS[held] ?? accent, cellSize);
      }
      drawCell(index);
      void pop(g, { scale: 1.07 });
      settleToken++;
      maybeFinish();
    }

    /**
     * Dragging slides the strip, and a drag is told from a tap by DISTANCE.
     *
     * The distance test is the part that was missing rather than merely undocumented. Pixi fires
     * `pointertap` whenever the press and the release land on the same object, with no movement
     * threshold of its own — so every scroll that began on a cell and ended on it painted that cell
     * as well, and counted as a correction the child never made.
     */
    window_.eventMode = "static";
    window_.on("pointerdown", (e) => {
      dragged = false;
      if (maxScroll <= 0) return;
      dragFrom = e.global.x;
      dragScroll = scroll;
    });
    window_.on("globalpointermove", (e) => {
      if (dragFrom === null) return;
      if (Math.abs(e.global.x - dragFrom) > DRAG_SLOP) dragged = true;
      scroll = Math.min(maxScroll, Math.max(0, dragScroll - (e.global.x - dragFrom)));
      strip.position.x = -scroll;
    });
    const endDrag = () => {
      dragFrom = null;
    };
    window_.on("pointerup", endDrag);
    window_.on("pointerupoutside", endDrag);

    layout();
    app.renderer.on("resize", layout);

    if (!prefersReducedMotion()) {
      strip.children.forEach((child, i) => {
        child.scale.set(0);
        void idle(30 + i * 14).then(() => animate(220, (t) => child.scale.set(easeOutBack(t))));
      });
    }

    const attempt = new Promise<Attempt>((resolve) => {
      resolveAttempt = resolve;
    }).then(async (result) => {
      const correct = opts.scoreTrial(stimulus, result.painted);
      if (correct) {
        successChime();
        for (const cell of blanks) {
          const g = cells[cell];
          if (g) burst(fx, g.position.x - scroll, g.position.y, [accent, COLOR.correct], 6);
        }
        await idle(560);
      } else {
        missTone();
        const wrong = blanks.filter((cell) => result.painted[cell] !== stimulus.solution[cell]);
        await Promise.all(
          wrong.slice(0, 6).map((cell) => (cells[cell] ? nudge(cells[cell], 4) : Promise.resolve())),
        );
        await idle(240);
        // Corrected in place, so the child sees WHERE the strip went wrong rather than a fresh one.
        for (const cell of wrong) {
          painted[cell] = stimulus.solution[cell] ?? TILING_EMPTY;
          drawCell(cell);
          const g = cells[cell];
          if (g) glow(glowLayer, g.position.x - scroll, g.position.y, cellSize * 0.5, COLOR.correct, 420);
          await idle(160);
        }
        await idle(620);
      }
      return result;
    });

    return {
      attempt,
      /** Lets the demonstration paint a cell itself. The rule is shown, never stated. */
      simulatePaint: (cell: number, colour: number) => {
        held = colour;
        drawSwatches();
        onCell(cell);
      },
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: TilingTaskResult["reason"] = "completed";

  /**
   * A wordless demonstration, before anything is scored.
   *
   * Three things here are invisible and none of them can be said in words on this surface: that the
   * left of the strip is a picture to copy rather than a start you continue freely, that a swatch
   * is picked up before a cell is painted, and that a blank cell in the motif is meant to stay
   * blank. So the game fills one repeat itself, cell by cell, skipping the gaps.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateStimulus(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(demo);
      await idle(1000);
      for (const cell of tilingBlanks(demo)) {
        if (opts.signal?.aborted) break;
        const value = demo.solution[cell];
        // Blanks in the motif are skipped rather than filled, which is the whole point of showing
        // it: a gap is part of the pattern, not a cell nobody got to.
        if (value === undefined || value === TILING_EMPTY) continue;
        shown.simulatePaint(cell, value);
        await idle(420);
      }
      if (!opts.signal?.aborted) {
        await untilAbandoned(shown.attempt, opts.signal);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const stimulus = opts.generateStimulus(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The strip's shape, then the motif. Enough to rebuild the whole trial without this build.
        stimulus: [stimulus.cols, stimulus.rows, stimulus.motifCols, ...stimulus.given],
        options: [stimulus.solution],
        correctIndex: 0,
      });

      const start = performance.now();
      const trial = present(stimulus);
      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const correct = opts.scoreTrial(stimulus, outcome.painted);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        // The whole strip, motif included, so the response and the solution are the same shape and
        // a later reading does not have to know which cells were printed.
        response: outcome.painted,
        correct,
        responseMs: elapsed,
        // Repaints: a child who carried the motif straight through and one who filled the strip
        // twice both finish it, and they are not the same performance.
        corrections: outcome.clears,
      });

      const fast = detectImplausibleSpeed(elapsed, tilingBlanks(stimulus).length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
